import { useState } from 'react';
import { CODE_TABS, type CodeTab } from '../lib/generators';
import type { ColumnSchema } from '../lib/types';
import type { SchemaTemplate } from '../lib/storage';
import { copyText } from './ui';

interface CodePanelProps {
  tab: CodeTab;
  onTab: (tab: CodeTab) => void;
  code: string;
  tableName: string;
  onTableName: (name: string) => void;
  columns: ColumnSchema[];
  isPro: boolean;
  templates: SchemaTemplate[];
  onSaveTemplate: (name: string) => void;
  onApplyTemplate: (template: SchemaTemplate) => void;
  onDeleteTemplate: (id: string) => void;
  onUpgrade: () => void;
}

export function CodePanel({
  tab,
  onTab,
  code,
  tableName,
  onTableName,
  columns,
  isPro,
  templates,
  onSaveTemplate,
  onApplyTemplate,
  onDeleteTemplate,
  onUpgrade,
}: CodePanelProps) {
  const [copied, setCopied] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const lineCount = code ? code.split('\n').length : 0;

  const handleCopy = async () => {
    const ok = await copyText(code);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 shadow-panel sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-100">Code generator</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Copy-paste ready output built from the inferred schema
            {columns.some((column) => column.nullCount > 0) ? ' · nullable columns marked' : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <span className="hidden sm:inline">table / model name</span>
            <input
              value={tableName}
              onChange={(event) => onTableName(event.target.value)}
              spellCheck={false}
              className="w-40 rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1.5 font-mono text-xs text-slate-100 outline-none transition focus:border-amber-500 focus:ring-1 focus:ring-amber-500/40"
            />
          </label>
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-900 transition hover:bg-white"
          >
            {copied ? (
              <>
                <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 text-lime-600" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z"
                    clipRule="evenodd"
                  />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="currentColor">
                  <path d="M7 2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V6.4A2 2 0 0 0 12.6 5L10 2.4A2 2 0 0 0 8.6 2H7Z" />
                  <path d="M5 6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1H7a3 3 0 0 1-3-3V6Z" />
                </svg>
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5" role="tablist">
        {CODE_TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            title={entry.blurb}
            onClick={() => onTab(entry.id)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
              tab === entry.id
                ? 'bg-amber-500 text-slate-950'
                : 'border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white'
            }`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="relative mt-3">
        <pre className="scrollbar-thin max-h-[460px] overflow-auto rounded-xl border border-slate-800 bg-slate-950 p-4 font-mono text-[11.5px] leading-relaxed text-slate-200">
          <code>{code}</code>
        </pre>
        <span className="pointer-events-none absolute bottom-2 right-3 rounded bg-slate-900/90 px-1.5 py-0.5 text-[10px] text-slate-500">
          {lineCount.toLocaleString()} lines
        </span>
      </div>

      {/* Saved schema templates (Pro) */}
      <div className="mt-5 rounded-xl border border-slate-800 bg-slate-950/40 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Saved schema templates
            </h3>
            <p className="mt-1 text-[11px] text-slate-500">
              Save this schema, then re-apply it to a future file to reuse the same types.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              placeholder="template name"
              spellCheck={false}
              className="w-40 rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1.5 text-xs text-slate-100 outline-none placeholder:text-slate-600 focus:border-amber-500"
            />
            <button
              type="button"
              onClick={() => {
                if (!isPro) {
                  onUpgrade();
                  return;
                }
                onSaveTemplate(templateName.trim() || tableName);
                setTemplateName('');
              }}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                isPro
                  ? 'bg-amber-500 text-slate-950 hover:bg-amber-400'
                  : 'border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white'
              }`}
            >
              {isPro ? 'Save template' : '🔒 Save template'}
            </button>
          </div>
        </div>

        {templates.length === 0 ? (
          <p className="mt-3 text-[11px] text-slate-600">No templates saved yet.</p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {templates.map((template) => (
              <li
                key={template.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2"
              >
                <span className="text-xs font-medium text-slate-200">{template.name}</span>
                <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-400">
                  {template.columns.length} columns
                </span>
                <span className="text-[10px] text-slate-600">
                  {new Date(template.createdAt).toLocaleDateString()}
                </span>
                <span className="ml-auto flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onApplyTemplate(template)}
                    className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 transition hover:border-amber-500/60 hover:text-amber-300"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteTemplate(template.id)}
                    className="rounded border border-slate-800 px-2 py-1 text-[11px] text-slate-500 transition hover:border-rose-500/60 hover:text-rose-300"
                  >
                    Delete
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
